import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
    CalendarBlank,
    Users,
    Exam,
    UserPlus,
    GraduationCap,
    ChatCircleDots,
    CalendarX,
    Gift,
    CaretRight,
    Clock,
    CheckCircle,
    Hourglass,
    Bell,
    Coins,
} from '@phosphor-icons/react';
import PendingCancellationsCard from './PendingCancellationsCard';
import PendingSignupsCard from './PendingSignupsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatLessonDate(dateStr) {
    if (!dateStr) return '';
    try {
        const d = new Date(`${dateStr}T00:00:00`);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
        const target = new Date(d); target.setHours(0, 0, 0, 0);
        if (target.getTime() === today.getTime()) return 'Vandaag';
        if (target.getTime() === tomorrow.getTime()) return 'Morgen';
        return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
        return dateStr;
    }
}

function formatToday() {
    try {
        const d = new Date();
        const s = d.toLocaleDateString('nl-NL', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
        return s.charAt(0).toUpperCase() + s.slice(1);
    } catch {
        return '';
    }
}

function greetingFor(name) {
    const h = new Date().getHours();
    let greet = 'Welkom terug';
    if (h < 6) greet = 'Goedenacht';
    else if (h < 12) greet = 'Goedemorgen';
    else if (h < 18) greet = 'Goedemiddag';
    else greet = 'Goedenavond';
    return name ? `${greet}, ${name.split(' ')[0]}` : greet;
}

function ActionTile({ icon: Icon, iconColor, title, subtitle, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            className="group bg-[#2D3748] hover:bg-[#3D4A5C] border border-[#4A5568] hover:border-[#D4A537]/40 rounded-2xl p-4 text-left transition-all active:scale-[0.97] flex flex-col gap-3"
            data-testid={testId}
        >
            <div
                className="w-11 h-11 rounded-full flex items-center justify-center transition-transform group-hover:scale-105"
                style={{
                    backgroundColor: iconColor,
                    boxShadow: `0 4px 14px -4px ${iconColor}99`,
                }}
            >
                <Icon size={22} weight="fill" className="text-white" />
            </div>
            <div className="min-w-0">
                <div className="text-base font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {title}
                </div>
                <div className="text-xs text-gray-400 truncate mt-0.5">{subtitle}</div>
            </div>
        </button>
    );
}

export default function HomeTab({
    user,
    isInstructor,
    students = [],
    scheduledLessons = [],
    stats,
    onGoTab,
    onAddStudent,
    onSelectStudent,
    onOpenAdministratie,
}) {
    const navigate = useNavigate();
    const dateText = formatToday();
    const greeting = greetingFor(user?.name);

    // Compute today's lessons
    const todayStr = new Date().toISOString().slice(0, 10);
    const todayLessons = useMemo(() => {
        return (scheduledLessons || [])
            .filter(l => l.date === todayStr)
            .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    }, [scheduledLessons, todayStr]);

    // Upcoming lessons (future, capped to next 5 for the student preview)
    const now = new Date();
    const upcomingLessons = useMemo(() => {
        return (scheduledLessons || [])
            .filter(l => {
                try {
                    const end = new Date(`${l.date}T${l.time || '00:00'}:00`);
                    end.setMinutes(end.getMinutes() + (l.duration_minutes || 60));
                    return end > now;
                } catch { return false; }
            })
            .sort((a, b) =>
                (a.date || '').localeCompare(b.date || '') ||
                (a.time || '').localeCompare(b.time || '')
            );
    }, [scheduledLessons]);

    const upcomingCount = upcomingLessons.length;

    // For students: list to render (today first, fall back to upcoming).
    // For instructors: keep "Planning vandaag" only.
    const lessonsToShow = isInstructor ? todayLessons : upcomingLessons.slice(0, 5);
    const showingTodayHeader = isInstructor || (upcomingLessons.length > 0 && upcomingLessons[0].date === todayStr);

    // Local optimistic state for confirm clicks so the badge flips immediately
    const [confirmedLocal, setConfirmedLocal] = useState({});
    const [confirming, setConfirming] = useState(null);

    const confirmLesson = async (lessonId) => {
        if (confirming) return;
        setConfirming(lessonId);
        try {
            await axios.post(`${API}/scheduled-lessons/${lessonId}/confirm`, null, { withCredentials: true });
            setConfirmedLocal(prev => ({ ...prev, [lessonId]: true }));
            toast.success('Les bevestigd ✅');
        } catch {
            toast.error('Bevestigen mislukt — probeer opnieuw');
        }
        setConfirming(null);
    };

    // Instructor-only: "Ik sta voor de deur 🚗" → pings the student
    const [pickupPinging, setPickupPinging] = useState(null);
    const [pickupDoneIds, setPickupDoneIds] = useState({});
    const pingPickup = async (lesson) => {
        if (pickupPinging) return;
        setPickupPinging(lesson.id);
        try {
            const { data } = await axios.post(
                `${API}/scheduled-lessons/${lesson.id}/at-pickup`,
                null,
                { withCredentials: true }
            );
            if (data?.delivered === false) {
                toast.info('Externe leerling — geen melding verstuurd');
            } else {
                toast.success('Leerling krijgt een melding 🚗');
                setPickupDoneIds(prev => ({ ...prev, [lesson.id]: Date.now() }));
            }
        } catch {
            toast.error('Melding versturen mislukt');
        }
        setPickupPinging(null);
    };

    // Exams this week (instructor only)
    const examsThisWeek = useMemo(() => {
        if (!isInstructor) return 0;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7);
        return (students || []).filter(s => {
            if (!s.exam_date) return false;
            const d = new Date(s.exam_date); d.setHours(0, 0, 0, 0);
            return d >= today && d <= weekEnd;
        }).length;
    }, [students, isInstructor]);

    // Tiles config — role aware
    const tiles = isInstructor ? [
        {
            icon: CalendarBlank,
            iconColor: '#3B82F6',
            title: 'Planning',
            subtitle: `${upcomingCount} ${upcomingCount === 1 ? 'les' : 'lessen'} gepland`,
            onClick: () => onGoTab?.('agenda'),
            testId: 'home-tile-planning',
        },
        {
            icon: Users,
            iconColor: '#10B981',
            title: 'Leerlingen',
            subtitle: `${students.length} ${students.length === 1 ? 'leerling' : 'leerlingen'}`,
            onClick: () => onGoTab?.('leerlingen'),
            testId: 'home-tile-students',
        },
        {
            icon: GraduationCap,
            iconColor: '#D4A537',
            title: 'Examens',
            subtitle: `${examsThisWeek} deze week`,
            onClick: () => onGoTab?.('examens'),
            testId: 'home-tile-exams',
        },
        {
            icon: UserPlus,
            iconColor: '#A78BFA',
            title: 'Nieuwe leerling',
            subtitle: 'Voeg toe aan systeem',
            onClick: () => onAddStudent?.(),
            testId: 'home-tile-new-student',
        },
        {
            icon: Coins,
            iconColor: '#F59E0B',
            title: 'Administratie',
            subtitle: 'Wie moet betalen?',
            onClick: () => onOpenAdministratie?.(),
            testId: 'home-tile-administratie',
        },
    ] : [
        {
            icon: GraduationCap,
            iconColor: '#D4A537',
            title: 'Mijn leskaart',
            subtitle: 'Bekijk je voortgang',
            onClick: () => onGoTab?.('leskaart'),
            testId: 'home-tile-card',
        },
        {
            icon: ChatCircleDots,
            iconColor: '#3B82F6',
            title: 'Chat',
            subtitle: 'Stuur een bericht',
            onClick: () => window.dispatchEvent(new CustomEvent('qr:open-chat')),
            testId: 'home-tile-chat',
        },
        {
            icon: CalendarX,
            iconColor: '#EF4444',
            title: 'Les annuleren',
            subtitle: 'Verzuim doorgeven',
            onClick: () => window.dispatchEvent(new CustomEvent('qr:open-cancel')),
            testId: 'home-tile-cancel',
        },
        {
            icon: Gift,
            iconColor: '#10B981',
            title: 'Gratis les',
            subtitle: 'Verdien een gratis rijles',
            onClick: () => window.dispatchEvent(new CustomEvent('qr:open-referral')),
            testId: 'home-tile-referral',
        },
    ];

    return (
        <div className="space-y-6 animate-fade-in-up" data-testid="home-tab">
            {/* Welcome header */}
            <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {greeting}
                </h1>
                <p className="text-sm text-gray-400 mt-1 capitalize-first">{dateText}</p>
            </div>

            {/* Pending cancellations — instructors only */}
            {isInstructor && <PendingSignupsCard />}
            {isInstructor && <PendingCancellationsCard />}

            {/* Quick actions grid */}
            <div>
                <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-widest mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Snelle acties
                </h2>
                <div className="grid grid-cols-2 gap-3">
                    {tiles.map((t) => (
                        <ActionTile key={t.testId} {...t} testId={t.testId} />
                    ))}
                </div>
            </div>

            {/* Upcoming lessons */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-widest" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {isInstructor ? 'Planning vandaag' : (showingTodayHeader ? 'Jouw lessen' : 'Komende lessen')}
                    </h2>
                    {lessonsToShow.length > 0 && (
                        <button
                            onClick={() => onGoTab?.(isInstructor ? 'agenda' : 'leskaart')}
                            className="text-xs text-[#D4A537] hover:text-[#E5B84A] inline-flex items-center gap-0.5"
                            data-testid="home-view-all-lessons"
                        >
                            Bekijk alles <CaretRight size={11} weight="bold" />
                        </button>
                    )}
                </div>

                {lessonsToShow.length === 0 ? (
                    <div className="bg-[#2D3748] border border-[#4A5568] rounded-2xl py-10 text-center">
                        <div className="w-14 h-14 rounded-full bg-[#3D4A5C] mx-auto mb-3 flex items-center justify-center">
                            <CalendarBlank size={26} className="text-gray-500" />
                        </div>
                        <p className="text-sm text-gray-400">
                            {isInstructor ? 'Geen lessen meer vandaag' : 'Nog geen lessen ingepland'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {lessonsToShow.map(l => {
                            const studentObj = students.find(s => s.id === l.student_id) || null;
                            const sname = studentObj?.name;
                            const isExternal = !!studentObj?.external;
                            const pending = !confirmedLocal[l.id] && l.confirmation_status === 'pending';
                            const dateLabel = formatLessonDate(l.date);
                            const isToday = l.date === todayStr;
                            const canOpenCard = isInstructor && studentObj && !isExternal && onSelectStudent;
                            const lessonStart = (() => {
                                try { return new Date(`${l.date}T${l.time || '00:00'}:00`); } catch { return null; }
                            })();
                            const minsUntil = lessonStart ? (lessonStart - now) / 60000 : null;
                            // Show pickup button from 30 min before lesson until 15 min after start
                            const showPickup = isInstructor && !isExternal && minsUntil != null && minsUntil <= 30 && minsUntil >= -15;
                            const pickedUp = !!pickupDoneIds[l.id];
                            return (
                                <div
                                    key={l.id}
                                    className={`bg-[#2D3748] border rounded-xl p-3 ${pending && !isInstructor ? 'border-amber-400/60 ring-1 ring-amber-400/20' : 'border-[#4A5568]'} ${canOpenCard ? 'transition-colors hover:border-[#D4A537]/40 hover:bg-[#3D4A5C]' : ''}`}
                                    data-testid={`home-lesson-${l.id}`}
                                >
                                    <div
                                        className={`flex items-center gap-3 ${canOpenCard ? 'cursor-pointer' : ''}`}
                                        onClick={canOpenCard ? () => onSelectStudent(studentObj) : undefined}
                                        role={canOpenCard ? 'button' : undefined}
                                        tabIndex={canOpenCard ? 0 : undefined}
                                        onKeyDown={canOpenCard ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectStudent(studentObj); } } : undefined}
                                        data-testid={canOpenCard ? `home-lesson-open-card-${l.id}` : undefined}
                                    >
                                        <div className="flex flex-col items-center justify-center w-16 flex-shrink-0">
                                            <div className={`text-base font-bold leading-none ${isToday ? 'text-[#D4A537]' : 'text-white'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                {l.time || '--'}
                                            </div>
                                            <div className="text-[10px] text-gray-500 mt-0.5">{l.duration_minutes || 60} min</div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-sm font-medium text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                    {isInstructor ? (sname || 'Leerling') : 'Jouw rijles'}
                                                </span>
                                                <span className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 ${isToday ? 'bg-[#D4A537]/20 text-[#D4A537]' : 'bg-[#1A202C] text-gray-400 border border-[#4A5568]'}`}>
                                                    {dateLabel}
                                                </span>
                                                {isExternal && (
                                                    <span className="text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-orange-500/20 text-orange-300 border border-orange-400/40">
                                                        Extern
                                                    </span>
                                                )}
                                            </div>
                                            {l.notes ? (
                                                <div className="text-xs text-gray-400 truncate mt-0.5">{l.notes}</div>
                                            ) : (
                                                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                                    <Clock size={11} />
                                                    Rijles ingepland
                                                </div>
                                            )}
                                        </div>
                                        {pending ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-400/40 rounded-full px-1.5 py-0.5 flex-shrink-0">
                                                <Hourglass size={9} weight="fill" />
                                                Wacht
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-400/40 rounded-full px-1.5 py-0.5 flex-shrink-0">
                                                <CheckCircle size={9} weight="fill" />
                                                OK
                                            </span>
                                        )}
                                        {canOpenCard && (
                                            <CaretRight size={14} weight="bold" className="text-gray-500 flex-shrink-0" />
                                        )}
                                    </div>

                                    {/* Confirm button — only for students with a pending lesson */}
                                    {!isInstructor && pending && (
                                        <button
                                            onClick={() => confirmLesson(l.id)}
                                            disabled={confirming === l.id}
                                            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4A537] hover:bg-[#E5B84A] disabled:opacity-60 text-[#1A202C] font-semibold py-2 transition-colors active:scale-[0.98]"
                                            data-testid={`home-confirm-${l.id}`}
                                            style={{ fontFamily: 'Outfit, sans-serif' }}
                                        >
                                            <CheckCircle size={16} weight="fill" />
                                            {confirming === l.id ? 'Bezig…' : 'Bevestig deze les'}
                                        </button>
                                    )}

                                    {/* Pickup ping button — only for instructors, near lesson time */}
                                    {showPickup && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); pingPickup(l); }}
                                            disabled={pickupPinging === l.id}
                                            className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg font-semibold py-2 transition-colors active:scale-[0.98] ${pickedUp ? 'bg-emerald-500/15 border border-emerald-400/40 text-emerald-300' : 'bg-[#3B82F6] hover:bg-[#2563EB] text-white'} disabled:opacity-60`}
                                            data-testid={`home-pickup-${l.id}`}
                                            style={{ fontFamily: 'Outfit, sans-serif' }}
                                        >
                                            <Bell size={16} weight="fill" />
                                            {pickupPinging === l.id
                                                ? 'Versturen…'
                                                : pickedUp
                                                    ? 'Melding verstuurd ✓'
                                                    : 'Ik sta voor de deur 🚗'}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Mini stats footer for instructor (extra context) */}
            {isInstructor && stats && (
                <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="bg-[#2D3748] border border-[#4A5568] rounded-xl p-3">
                        <div className="text-[10px] text-gray-500 uppercase tracking-widest">Totaal lessen</div>
                        <div className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {stats.lessons_count || 0}
                        </div>
                    </div>
                    <div className="bg-[#2D3748] border border-[#4A5568] rounded-xl p-3">
                        <div className="text-[10px] text-gray-500 uppercase tracking-widest">Actief</div>
                        <div className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {students.filter(s => s.status !== 'archived').length}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
